import React, { useState, useRef, useEffect } from "react";
import { CircularProgress } from "@mui/material";
import TableHeader from "../../../common/table/TableHeader";
import axiosInstance from "../../../../utils/AxiosInstance";
import ReactPaginate from "react-paginate";
import useShowAlert from "../../../../utils/AlertUtiles";
import handleError from "../../../../constant/handleError";
import checkNetworkStatus from "../../../common/networkStatus/CheckNetworkStatus";
import uRLs from "../../../../utils/baseUtils";
import DateConverter from "@heartinz/date_and_time_processor";
import TableSkeleton from "../../../common/table/TableSkeleton";
import DeletePopup from "../../../../constant/DeletePopup";
import { useEscapeKeyContext } from "../../../../contexts/UseEscapKeyContext";
import { removeAllWhitespace, sanitizeNumberInput } from "../../../../utils/helper";

const PincodeManagement = () => {
  const [errors, setErrors] = useState(false); 
  const [open, setOpen] = useState(false);
  const [newRow, setNewRow] = useState({ pincode: "", price: "" });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState({
    add: false,
    list: false,
  });
  const {useEscapeKey}=useEscapeKeyContext();
  const pincodeRef = useRef(null);
  const priceRef = useRef(null);
  const saveButtonRef = useRef(null); 

  const showAlert = useShowAlert(); 

  const headers = ["S.No", "Date", "Pincode", "Price/Acre", "Action"];

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const handleClose = () => {
    setOpen(false);
    setIsEditMode(false)
    setErrors("");
    setNewRow({ pincode: "", price: "" });
  };

  const confirmDelete = () => {
    handleDelete(itemToDelete);
    setIsDeleteDialogOpen(false);
  };

  const openDeleteModal = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };
const validateRow = (row) => {
  const errors = {};
   if (!row.pincode) {
    errors.price = "pincode cannot be empty.";
  }
  else if (!/^\d{6}$/.test(row.pincode)) {
    errors.pincode = "Pincode must be a 6-digit number.";
  } else if (!row.price) {
    errors.price = "Price cannot be empty.";
  } else if (isNaN(Number(row.price))) {
    errors.price = "Price must be a valid number.";
  }
  return errors;
};
  const handleAddRow = async (e) => {
    e.preventDefault();
   const validationErrors = validateRow(newRow);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);     
      if (validationErrors.pincode) pincodeRef.current?.focus();
      else if (validationErrors.price) priceRef.current?.focus();
      return;
    }

    if (!newRow.pincode || !newRow.price) return;
    setLoading((prev) => ({ ...prev, add: true }));
    try {
      const data = {
        pincode: newRow.pincode,
        pricePerAcre: Number(newRow.price),
      };
      const response = await axiosInstance.post(`settings/admin/pincode/`, data);
      if (response && response?.data?.status && response?.data?.results) {
        const updated = [response?.data?.results, ...rows].slice(0, uRLs.PER_PAGE);;
        setRows(updated);
        setTotalItems((prev) => prev + 1);
        handleClose();
      }
      showAlert("success", response?.data?.message);
    } catch (error) {
     if (error.response?.data?.code === "ALREADY_EXIST") {
      setErrors({ pincode: "This pincode already exists." });
      pincodeRef.current?.focus();      
    } else {
      handleError(error, showAlert);
    }
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, add: false }));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const validationErrors = validateRow(newRow);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);     
      if (validationErrors.pincode) pincodeRef.current?.focus();
      else if (validationErrors.price) priceRef.current?.focus();
      return;
    }    
    if (!newRow.pincode || !newRow.price) return;

    try {
      const data = {
        pincode: newRow.pincode,
        pricePerAcre: Number(newRow.price),
      };
      const response = await axiosInstance.patch(`settings/admin/pincode/${editId}`, data);
      if (response && response?.data?.status && response?.data?.results) {
        const newItem = response.data.results;
        const formatted = {
          _id: newItem._id,
          pincode: newItem.pincode,
          pricePerAcre: newItem.pricePerAcre,
          createdAt: newItem.createdAt,
        };

        setRows((prev) => prev.map((r) => (r._id === editId ? formatted : r)));
        handleClose();
      }
      showAlert("success", response?.data?.message);
    } catch (error) {
      if (error.response?.data?.code === 11000) {
      setErrors({ pincode: "This pincode already exists." });
      pincodeRef.current?.focus();      
    } else {
      handleError(error, showAlert);
    }
      return false;
    }
  };

  const handleDelete = async (id) => {
    setLoading((prev) => ({ ...prev, delete: true }));
    try {
      const response = await axiosInstance.delete(`settings/admin/pincode/${id}`);
      if (response && response.data.results) {
        showAlert("success", response?.data?.message);
        setRows((currentItems) =>
          currentItems.filter((item) => item._id !== id)
        );
        setTotalItems((prev) => Math.max(prev - 1, 0));
      }
    } catch (error) {
      handleError(error, showAlert);
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const stopEventPropagation = (e) => e.stopPropagation();

  const fetchListPincode = async (page = 0) => {
    setLoading((prev) => ({ ...prev, list: true }));
    checkNetworkStatus();

    try {
      const response = await axiosInstance.get(
        `settings/admin/pincode/?pageno=${page + 1}&limit=${uRLs.PER_PAGE}`
      );
      if (
        response &&
        response?.data?.status &&
        response?.data?.results?.length > 0
      ) {
        setRows(response?.data?.results);
      } else {
        setRows([]);
      }
      if (response?.data?.results?.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response?.data.totalPages || 0);
        setTotalItems(response?.data?.totalItems || 0);
      }
    } catch (error) {
      setRows([]);
      setTotalPages(0);
      handleError(error, showAlert);
      return false;
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const handleEnterKey = (e, nextRef = null) => {   
    if (e.key === 'Enter') {
      e.preventDefault();

      if (nextRef && nextRef.current) {
        
        nextRef.current.focus();
      } else if (e.currentTarget === priceRef.current) {
       
        saveButtonRef.current?.click();
      }
    }
  };
  const handleEscapeKeyPress = () => {
    if (isDeleteDialogOpen) {     
      setIsDeleteDialogOpen(false);
    } else if (open) {   
      handleClose();
    } 
  };
  useEscapeKey(handleEscapeKeyPress)
  useEffect(() => {
    fetchListPincode(currentPage);
  }, [currentPage]);

  useEffect(() => {
  if (open && pincodeRef.current) {
    setTimeout(() => pincodeRef.current.focus(), 200);
  }
}, [open]);

  return (
    <>
      <div className="d-flex align-items-center justify-content-between">
        <div className="breadcrumb-title pe-3 mb-3">Pincode Management</div>
        <button
          className="btn btn-primary text-right"
          onClick={() => {
            setOpen(true);
          }}
        >
          <i className="bx bx-plus"></i> Add
        </button>
      </div>

      <div className="col-col-12 mt-3">
        <div className="card mb-0">
          <div className="card-body p-0">
            <table className="table align-middle table-hover mb-0 cursor-pointer ">
              <TableHeader headers={headers} />

              <tbody>
                {loading.list ? (
                  <TableSkeleton rows={5} columns={[300, 200, 200, 200, 100]} />
                ) : Array.isArray(rows) && rows.length > 0 ? (
                  rows.map((item, index) => (
                    <tr
                      key={item.pincode}                     
                    >
                      <td className="text-center">
                        {totalItems - (currentPage * uRLs.PER_PAGE + index)}
                      </td>
                      <td className="text-center">
                        {" "}
                        {
                          DateConverter.ISOToDisplayFormat(
                            DateConverter.convertToTimeZone(
                              item?.createdAt,
                              uRLs.TIMEZONE
                            )
                          ).date
                        }
                      </td>
                      <td className="text-center"> {item.pincode}</td>
                      <td className="text-center">₹{item?.pricePerAcre}</td>
                      <td className="order-actions d-flex justify-content-center align-item-center">
                        <button
                          type="button"
                          className="btn-label-primary shadow-none me-2"
                          onClick={() => {
                            setIsEditMode(true);
                            setEditId(item._id);
                            setNewRow({
                              pincode: item.pincode,
                              price: item.pricePerAcre,
                            });
                            setOpen(true);
                          }}
                        >
                          <i className="bx bx-pencil"></i>
                        </button>
                        <button
                          type="button"
                          className="btn-label-danger shadow-none me-2"
                          onClick={() => openDeleteModal(item?._id)}
                        >
                          <i className="bx bx-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center p-2 text-muted">
                      <span className="text-muted">
                        No pincode found in this list.
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {isDeleteDialogOpen && (
            <DeletePopup
              buttonloading={loading.delete}
              onClose={() => setIsDeleteDialogOpen(false)}
              onConfirm={confirmDelete}
              Name=" Pincode"
              message={`Do you want to delete these records? <br>This process cannot be undone.`}
            />
          )}
          {totalPages > 0 && (
            <div className="d-flex justify-content-center mt-3">
              <ReactPaginate
                previousLabel={"← Previous"}
                nextLabel={"Next →"}
                breakLabel={"..."}
                onPageChange={({ selected }) => {
                  setLoading((prev) => ({ ...prev, list: true }));
                  setCurrentPage(selected);
                }}
                pageRangeDisplayed={5}
                pageCount={totalPages}
                containerClassName="pagination"
                pageClassName="page-item"
                pageLinkClassName="page-link"
                previousClassName="page-item"
                previousLinkClassName="page-link"
                nextClassName="page-item"
                nextLinkClassName="page-link"
                breakClassName="page-item"
                breakLinkClassName="page-link"
                activeClassName="active"
                forcePage={currentPage}
              />
            </div>
          )}
        </div>
      </div>

      {open && (
        <>
          <div>
            <div className="modal-backdrop show"></div>
            <div
              className="modal show d-block"
              id="PincodeModal"
              tabIndex="0"
              aria-modal="true"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable"
                onClick={stopEventPropagation}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h6 className="modal-title">
                      {isEditMode ? "Edit" : "Add"} Pincode/Price
                    </h6>
                    <button
                      type="button"
                      className="btn-close border"
                      onClick={handleClose}
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="row">
                      <div className=" ">
                        <div className="card-body">
                          <div className="row g-3">
                            <div className="col-lg-12">
                              <label className="form-label">
                                Pincode <span className="text-danger">*</span>
                              </label>

                              <input
                                type="text"
                                ref={pincodeRef}
                                className="form-control me-2"
                                autoComplete="off"
                                placeholder="New Pincode (e.g., 695001)"
                                value={newRow.pincode}
                                maxLength={6}
                                onChange={(e) => {
                                  setErrors("");
                                  setNewRow((s) => ({
                                    ...s,
                                    pincode: removeAllWhitespace(e.target.value),
                                  }));
                                }}
                                 onKeyDown={(e) => handleEnterKey(e, priceRef)}                         
                              />
                              {errors.pincode && (
                                <div className="error-message invalid-feedback">
                                  {errors.pincode}
                                </div>
                              )}
                            </div>
                            <div className="col-lg-12">
                              <label className="form-label">
                                Price<span className="text-danger">*</span>
                              </label>

                              <input
                                type="text"
                                ref={priceRef}
                                className="form-control"
                                autoComplete="off"
                                placeholder="Price per Acre (e.g., 15000)"
                                value={newRow.price}
                                onChange={(e) => {
                                  setErrors("");
                                  setNewRow((s) => ({
                                    ...s,
                                    price: sanitizeNumberInput(e.target.value),
                                  }));
                                }}
                                onKeyDown={(e) => handleEnterKey(e, saveButtonRef)}
                              />
                              {errors.price && (
                                <div className="error-message invalid-feedback">
                                  {errors.price}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer d-flext justify-content-between">
                    <button
                      type="button"                      
                      className="btn btn-outline-secondary text-left"
                      onClick={handleClose}
                    >
                      <i className="bx bx-x"></i> Close
                    </button>

                    {loading.add ? (
                      <button
                        className="btn btn-primary pb-0 text-right"
                        disabled
                      >
                        <CircularProgress size={22} className="text-white" />
                      </button>
                    ) : (
                      <button
                        type="submit"
                        ref={saveButtonRef}
                        className="btn btn-primary text-right"
                        // disabled={!newRow.pincode || !newRow.price}
                        onClick={isEditMode ? handleUpdate : handleAddRow}
                      >
                        {isEditMode ? (
                          <i className="bx bx-check me-1" />
                        ) : (
                          <i className="bx bx-plus me-1" />
                        )}
                        {isEditMode ? "Update" : "Add"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default PincodeManagement;
